import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import { zaehleAusgeloest } from '../planungLogic';
import type { PflichtStand, ProjektMerkmale } from '../types';

/**
 * Dreiwertiger Schalter: Ja / Nein / noch offen.
 *
 * Eine gewoehnliche Checkbox waere hier falsch. Sie kennt nur an und aus, und
 * „aus" hiesse dann „trifft nicht zu" — dabei ist der Normalfall zu Beginn
 * „weiss ich noch nicht". Der Unterschied ist keine Feinheit: ein
 * ungeklaertes Merkmal LAESST die zugehoerigen Fragen stehen, ein
 * ausdrueckliches Nein blendet sie aus.
 */
export const DreiWeg: React.FC<{
    label: string;
    wert: boolean | undefined;
    disabled: boolean;
    onChange: (w: boolean | undefined) => void;
}> = ({ label, wert, disabled, onChange }) => {
    const knopf = (eigen: boolean | undefined, text: string, farbe: string) => {
        const aktiv = wert === eigen;
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(eigen)}
                className={`px-2.5 py-1 text-xs font-bold rounded-md border transition-colors ${
                    aktiv ? farbe : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
                } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
            >
                {text}
            </button>
        );
    };
    return (
        <div className="flex items-center justify-between gap-3 py-1.5">
            <span className="text-sm text-slate-700 leading-snug">{label}</span>
            <div className="flex gap-1 shrink-0">
                {knopf(true, 'Ja', 'bg-rose-50 text-rose-700 border-rose-200')}
                {knopf(false, 'Nein', 'bg-slate-100 text-slate-600 border-slate-300')}
                {knopf(undefined, 'offen', 'bg-amber-50 text-amber-700 border-amber-200')}
            </div>
        </div>
    );
};

export const Zahlfeld: React.FC<{
    label: string;
    einheit?: string;
    wert: number | undefined;
    disabled: boolean;
    onChange: (w: number | undefined) => void;
    hinweis?: string;
}> = ({ label, einheit, wert, disabled, onChange, hinweis }) => (
    <label className="block">
        <span className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{label}</span>
        <div className="flex items-center gap-2">
            {/* `type="text"` mit inputMode statt `type="number"`: das
                Zahlenfeld frisst im Bestand nachweislich Zwischenstaende wie
                „2." und „0,25" (siehe Mindeststunden je Projekt-Leistung). */}
            <input
                type="text"
                inputMode="decimal"
                disabled={disabled}
                value={wert === undefined || wert === null ? '' : String(wert)}
                onChange={e => {
                    const roh = e.target.value.replace(',', '.').trim();
                    if (!roh) { onChange(undefined); return; }
                    const n = Number(roh);
                    onChange(isFinite(n) && n >= 0 ? n : undefined);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                placeholder="—"
            />
            {einheit && <span className="text-xs text-slate-400 shrink-0">{einheit}</span>}
        </div>
        {hinweis && <span className="block text-[11px] text-slate-400 mt-1">{hinweis}</span>}
    </label>
);

/**
 * Darstellung der drei Pflichtstaende. „nicht erforderlich" gibt es nur fuer
 * ein sicheres Nein — eine noch ungeklaerte Pflicht stand hier bisher genauso
 * da, und wer die Maske spaeter las, las eine Entwarnung.
 */
const PFLICHT_ANZEIGE: Record<PflichtStand, { text: string; farbe: string; icon: React.ReactNode }> = {
    pflicht: { text: 'erforderlich', farbe: 'text-rose-600', icon: <AlertTriangle className="w-4 h-4" /> },
    keine: { text: 'nicht erforderlich', farbe: 'text-slate-500', icon: <CheckCircle2 className="w-4 h-4" /> },
    ungeklaert: { text: 'noch nicht geklärt', farbe: 'text-amber-700', icon: <HelpCircle className="w-4 h-4" /> },
};

export const PflichtZeile: React.FC<{ titel: string; stand: PflichtStand; begruendung: string }> = ({ titel, stand, begruendung }) => {
    const anzeige = PFLICHT_ANZEIGE[stand] || PFLICHT_ANZEIGE.ungeklaert;
    return (
        <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
            <div className={`mt-0.5 shrink-0 ${stand === 'keine' ? 'text-slate-400' : anzeige.farbe}`}>
                {anzeige.icon}
            </div>
            <div className="min-w-0">
                <div className="text-sm font-bold text-slate-800">
                    {titel}: <span className={anzeige.farbe}>{anzeige.text}</span>
                </div>
                <div className="text-xs text-slate-500 leading-snug mt-0.5">{begruendung}</div>
            </div>
        </div>
    );
};

/**
 * Zaehler „N / von M Fragen / K haftungsrelevant" — wie viele Katalogfragen
 * die Merkmale gerade ausloesen. Steht rechts im Kopf der Pruefung; eigener
 * Baustein, damit die Station „Merkmale" im Leitfaden denselben Zaehler
 * zeigen kann.
 *
 * `lph` ist optional, gehoert aber dazu: ohne LPH zaehlt `zaehleAusgeloest`
 * Fragen spaeterer Leistungsphasen als ausgeloest statt als „erst in
 * spaeterer LPH". Die Pruefung reicht sie deshalb immer mit.
 */
export const AusgeloestZaehler: React.FC<{ merkmale: ProjektMerkmale; lph?: number | null }> = ({ merkmale, lph }) => {
    const vorschau = useMemo(() => zaehleAusgeloest(merkmale, undefined, lph), [merkmale, lph]);
    return (
        <div className="text-right shrink-0">
            <div className="text-2xl font-black text-slate-800">{vorschau.ausgeloest}</div>
            <div className="text-[11px] text-slate-400 leading-tight">
                von {vorschau.gesamt} Fragen<br />
                <span className="text-rose-500 font-bold">{vorschau.kritisch} haftungsrelevant</span>
                {vorschau.spaeter > 0 && (
                    <><br />{vorschau.spaeter} erst in späterer LPH</>
                )}
            </div>
        </div>
    );
};
